/**
 * Cmarkdownxychart — export utilities (CSV + PNG snapshot)
 */

import type { XYChartParsed } from './xychart-types';

const CSV_DOUBLE_QUOTE_ESCAPE = '""';
const CSV_DEFAULT_X_HEADER = 'Category';
const FILENAME_DEFAULT_STEM = 'chart';
const FILENAME_EXTENSION_CSV = 'csv';
const FILENAME_EXTENSION_PNG = 'png';
const SVG_NAMESPACE_URI = 'http://www.w3.org/2000/svg';
const SVG_XMLNS_ATTR = 'xmlns';
const SVG_WIDTH_ATTR = 'width';
const SVG_HEIGHT_ATTR = 'height';
const SVG_VIEWBOX_ATTR = 'viewBox';
const SVG_DATA_PREFIX = 'data-';
const FONT_FAMILY_PROPERTY = 'font-family';
const FONT_SIZE_PROPERTY = 'font-size';
const FONT_WEIGHT_PROPERTY = 'font-weight';
const SVG_MIME_TYPE = 'image/svg+xml;charset=utf-8';
const PNG_MIME_TYPE = 'image/png';
const CSV_MIME_TYPE = 'text/csv;charset=utf-8';
const CANVAS_2D_CONTEXT_ID = '2d';
const SNAPSHOT_DEFAULT_BACKGROUND = '#0E1320';

export interface XYChartImageSnapshot {
  svgDataUrl: string;
  filename: string;
  renderWidth: number;
  renderHeight: number;
  pixelRatio: number;
  background: string;
}

function escapeCsvField(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n') || value.includes('\r')) {
    return `"${value.replace(/"/g, CSV_DOUBLE_QUOTE_ESCAPE)}"`;
  }
  return value;
}

function formatNumberForCsv(value: number): string {
  if (Number.isInteger(value)) return String(value);
  return Number(value.toFixed(6)).toString();
}

/**
 * Format the parsed chart as a CSV string with the category axis as the
 * first column and one column per series.
 */
export function formatXYChartAsCsv(parsed: XYChartParsed): string {
  const xHeader = parsed.xAxis.title?.trim() || CSV_DEFAULT_X_HEADER;
  const headerRow = [
    escapeCsvField(xHeader),
    ...parsed.series.map((series) => escapeCsvField(series.label)),
  ].join(',');

  const dataRows = parsed.xAxis.categories.map((category, index) => {
    const cells = [
      escapeCsvField(category),
      ...parsed.series.map((series) => formatNumberForCsv(series.data[index] ?? 0)),
    ];
    return cells.join(',');
  });

  return [headerRow, ...dataRows].join('\r\n');
}

function buildSafeFilename(input: { title: string | null; extension: string }): string {
  const base = (input.title?.trim() || FILENAME_DEFAULT_STEM)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  const stem = base.length > 0 ? base : FILENAME_DEFAULT_STEM;
  return `${stem}.${input.extension}`;
}

/** Trigger a browser download from a blob. */
function triggerDownload(blob: Blob, filename: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function downloadXYChartCsv(parsed: XYChartParsed): void {
  const csv = formatXYChartAsCsv(parsed);
  // Prepend a UTF-8 BOM so spreadsheet apps interpret unicode correctly.
  const blob = new Blob(['﻿', csv], { type: CSV_MIME_TYPE });
  const filename = buildSafeFilename({ title: parsed.title, extension: FILENAME_EXTENSION_CSV });
  triggerDownload(blob, filename);
}

/**
 * Strip framer-motion and other transient runtime attributes from a cloned SVG
 * so the serialized form is renderable by an `<img>` tag (the snapshot path).
 *
 * Browsers will fail to load an SVG into an Image if the markup contains
 * invalid attributes; this conservatively removes attributes that begin with
 * `data-` or are known motion-only flags. It also inlines minimal computed
 * styles so the snapshot looks like the live chart even when dependent on
 * inherited CSS (font-family, fill defaults).
 */
function sanitizeSvgForSnapshot(input: {
  liveSvg: SVGSVGElement;
  cloned: SVGSVGElement;
}): void {
  const window = input.liveSvg.ownerDocument?.defaultView ?? null;
  const liveAll = input.liveSvg.querySelectorAll<SVGElement>('*');
  const clonedAll = input.cloned.querySelectorAll<SVGElement>('*');

  liveAll.forEach((liveNode, index) => {
    const target = clonedAll[index];
    if (!target) return;
    if (window) {
      const computed = window.getComputedStyle(liveNode);
      const fontFamily = computed.getPropertyValue(FONT_FAMILY_PROPERTY);
      const fontSize = computed.getPropertyValue(FONT_SIZE_PROPERTY);
      const fontWeight = computed.getPropertyValue(FONT_WEIGHT_PROPERTY);
      if (fontFamily) target.style.setProperty(FONT_FAMILY_PROPERTY, fontFamily);
      if (fontSize) target.style.setProperty(FONT_SIZE_PROPERTY, fontSize);
      if (fontWeight) target.style.setProperty(FONT_WEIGHT_PROPERTY, fontWeight);
    }
    Array.from(target.attributes)
      .filter((attribute) => attribute.name.startsWith(SVG_DATA_PREFIX))
      .forEach((attribute) => target.removeAttribute(attribute.name));
  });

  Array.from(input.cloned.attributes)
    .filter((attribute) => attribute.name.startsWith(SVG_DATA_PREFIX))
    .forEach((attribute) => input.cloned.removeAttribute(attribute.name));
}

export function prepareXYChartImageSnapshot(input: {
  svgElement: SVGSVGElement;
  parsed: XYChartParsed;
  pixelRatio?: number;
  background?: string;
}): XYChartImageSnapshot | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  const pixelRatio = input.pixelRatio ?? Math.min(window.devicePixelRatio || 1, 2);
  const background = input.background ?? SNAPSHOT_DEFAULT_BACKGROUND;

  const cloned = input.svgElement.cloneNode(true) as SVGSVGElement;
  cloned.setAttribute(SVG_XMLNS_ATTR, SVG_NAMESPACE_URI);

  const bbox = input.svgElement.getBoundingClientRect();
  const renderWidth = Math.max(1, Math.round(bbox.width));
  const renderHeight = Math.max(1, Math.round(bbox.height));
  cloned.setAttribute(SVG_WIDTH_ATTR, String(renderWidth));
  cloned.setAttribute(SVG_HEIGHT_ATTR, String(renderHeight));
  cloned.setAttribute(SVG_VIEWBOX_ATTR, `0 0 ${renderWidth} ${renderHeight}`);

  sanitizeSvgForSnapshot({ liveSvg: input.svgElement, cloned });

  const serialized = new XMLSerializer().serializeToString(cloned);
  const svgDataUrl = `data:${SVG_MIME_TYPE},${encodeURIComponent(serialized)}`;

  return {
    svgDataUrl,
    filename: buildSafeFilename({
      title: input.parsed.title,
      extension: FILENAME_EXTENSION_PNG,
    }),
    renderWidth,
    renderHeight,
    pixelRatio,
    background,
  };
}

export function renderXYChartImageSnapshot(input: {
  snapshot: XYChartImageSnapshot;
  image: HTMLImageElement;
}): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const canvas = document.createElement('canvas');
  canvas.width = Math.round(input.snapshot.renderWidth * input.snapshot.pixelRatio);
  canvas.height = Math.round(input.snapshot.renderHeight * input.snapshot.pixelRatio);
  const context = canvas.getContext(CANVAS_2D_CONTEXT_ID);
  if (!context) {
    return null;
  }

  context.scale(input.snapshot.pixelRatio, input.snapshot.pixelRatio);
  context.fillStyle = input.snapshot.background;
  context.fillRect(0, 0, input.snapshot.renderWidth, input.snapshot.renderHeight);
  context.drawImage(
    input.image,
    0,
    0,
    input.snapshot.renderWidth,
    input.snapshot.renderHeight,
  );
  return canvas.toDataURL(PNG_MIME_TYPE);
}
